import { getValueOfQueryParam } from '../../../utils/query-params'

export const DEEPLINK_PARAM = 'gmx-deeplink'
export const SCROLL_SEPARATOR = '::'

/**
 * Reads `?gmx-deeplink` on page load and either scrolls to, or scrolls-and-opens, the
 * matching `[data-deeplink-id]` element. Same-origin, same-path links that carry the
 * param are handled on click without a full navigation (URL is left unchanged).
 *
 * - `?gmx-deeplink=id` → scroll to `[data-deeplink-id="id"]`; if it has
 *   `data-overlay-reveal-id` also open that overlay.
 * - `?gmx-deeplink=id::scroll-id` → open the overlay for `id`, then scroll inside it
 *   to `[data-deeplink-id="scroll-id"]` once `gmx-overlay-shown` fires.
 *
 * Deeplink targets are resolved via `data-deeplink-id` only — never via HTML `id`.
 */
const INTERACTIVE_TAGS = new Set(['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'SUMMARY'])

type DeeplinkAnchor = HTMLAnchorElement | HTMLAreaElement

export class GMXDeeplinkSingleton {
	/** Injector shadow roots registered via `setupDeeplinks`, searched when a click's target is not in the link's own root. */
	private _injectedRoots = new Set<ShadowRoot>()

	constructor() {
		if (!window.gmxDeeplinkSingleton) {
			window.gmxDeeplinkSingleton = this
			document.addEventListener('click', this._onLinkClick)
			// Wait for all sub-resources to load so getBoundingClientRect() is stable.
			if (document.readyState === 'complete') {
				this.processDeeplink()
			} else {
				window.addEventListener('load', () => this.processDeeplink(), { once: true })
			}
		} else {
			window.console.log('Tried to instantiate deeplink singleton...already instantiated')
		}
	}

	/**
	 * Parse the deeplink query param and trigger the appropriate scroll/open behaviour.
	 * Public so tests can re-invoke it after changing `window.location.search`.
	 */
	public processDeeplink(): void {
		const rawValue = getValueOfQueryParam(DEEPLINK_PARAM)
		if (!rawValue) return

		this._resolve(rawValue)
	}

	/**
	 * Resolve the deeplink within an injected `root` (typically a `GMXInjector`'s
	 * `ShadowRoot`). The initial `processDeeplink()` pass only scans `document.body`,
	 * which — like all `querySelector` calls — cannot reach elements encapsulated in a
	 * shadow root. `GMXInjector` calls this after injecting its experience-fragment
	 * content so deeplink targets authored inside that shadow DOM are still handled,
	 * mirroring how the panel/overlay/tab/toast singletons accept the injector's root.
	 *
	 * No-op when there is no `gmx-deeplink` param or no matching `[data-deeplink-id]`
	 * exists in `root`, so it is safe to call from every injector regardless of which
	 * one (if any) actually owns the deeplink target.
	 */
	public setupDeeplinks(root: ShadowRoot | HTMLElement = document.body): void {
		if (root instanceof ShadowRoot) this._injectedRoots.add(root)

		const rawValue = getValueOfQueryParam(DEEPLINK_PARAM)
		if (!rawValue) return

		this._resolve(rawValue, root, true)
	}

	/**
	 * Parse `rawValue` and run scroll/open behaviour scoped to `root`.
	 * When `onlyIfInRoot` is true (injector pass), no-op unless the primary deeplink id
	 * exists under `root`.
	 */
	private _resolve(
		rawValue: string,
		root: ShadowRoot | HTMLElement = document.body,
		onlyIfInRoot = false,
	): void {
		const sepIdx = rawValue.indexOf(SCROLL_SEPARATOR)

		if (sepIdx !== -1) {
			const deeplinkId = rawValue.substring(0, sepIdx)
			const scrollTargetId = rawValue.substring(sepIdx + SCROLL_SEPARATOR.length)
			if (onlyIfInRoot && !this._findByDeeplinkId(root, deeplinkId)) return
			this._deeplinkOverlayWithScroll(deeplinkId, scrollTargetId, root)
		} else {
			if (onlyIfInRoot && !this._findByDeeplinkId(root, rawValue)) return
			this._deeplinkTarget(rawValue, root)
		}
	}

	private _onLinkClick = (e: MouseEvent): void => {
		if (e.defaultPrevented) return
		if (e.button !== 0) return
		if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return

		const anchor = this._findAnchorFromComposedPath(e)
		if (!anchor) return

		if (anchor.hasAttribute('download')) return

		const targetAttr =
			anchor.getAttribute('target') ?? document.querySelector('base[target]')?.getAttribute('target')
		if (targetAttr && targetAttr.toLowerCase() !== '_self') return

		// Overlay and panel triggers are opened by their own singletons, which re-dispatch clicks on them.
		if (anchor.closest('[data-overlay-reveal-id], [data-panel-reveal-id]')) return

		// Fragment-only and empty hrefs resolve against the current URL and would inherit its query.
		if (!this._hrefHasOwnQuery(anchor.getAttribute('href') ?? '')) return

		let url: URL
		try {
			url = new URL(anchor.href)
		} catch {
			return
		}

		if (url.origin !== location.origin || url.pathname !== location.pathname) return

		const rawValue = url.searchParams.get(DEEPLINK_PARAM)
		if (!rawValue) return

		e.preventDefault()

		const sepIdx = rawValue.indexOf(SCROLL_SEPARATOR)
		const lookupId = sepIdx !== -1 ? rawValue.substring(0, sepIdx) : rawValue

		this._resolve(rawValue, this._findRootContaining(lookupId, anchor.getRootNode()))
	}

	/**
	 * Return the nearest anchor or area on the click's composed path, or `null` if an
	 * interactive control (e.g. a button nested in an anchor-wrapped component) was
	 * activated first, since the anchor would not navigate in that case.
	 */
	private _findAnchorFromComposedPath(e: MouseEvent): DeeplinkAnchor | null {
		for (const node of e.composedPath()) {
			if (node instanceof HTMLAnchorElement || node instanceof HTMLAreaElement) return node
			if (node instanceof HTMLElement && INTERACTIVE_TAGS.has(node.tagName)) return null
		}
		return null
	}

	private _hrefHasOwnQuery(href: string): boolean {
		const queryIdx = href.indexOf('?')
		const hashIdx = href.indexOf('#')
		return queryIdx !== -1 && (hashIdx === -1 || queryIdx < hashIdx)
	}

	/**
	 * Pick the root that contains `[data-deeplink-id="deeplinkId"]`: the link's own root,
	 * then the light DOM, then any connected injector shadow root. Falls back to
	 * `document.body` so a missing target still goes through `_observeAndScroll`.
	 */
	private _findRootContaining(deeplinkId: string, anchorRoot: Node): ShadowRoot | HTMLElement {
		const candidates: (ShadowRoot | HTMLElement)[] = []
		if (anchorRoot instanceof ShadowRoot) candidates.push(anchorRoot)
		candidates.push(document.body)

		for (const root of this._injectedRoots) {
			if (!root.host.isConnected) {
				this._injectedRoots.delete(root)
			} else if (root !== anchorRoot) {
				candidates.push(root)
			}
		}

		return candidates.find(root => this._findByDeeplinkId(root, deeplinkId)) ?? document.body
	}

	/**
	 * Resolve a single deeplink id:
	 * 1. Has `data-expanded-content-path` → scroll, wait for scroll to settle, then open.
	 * 2. Has (or contains) a `data-panel-reveal-id` trigger (e.g. a gallery tile) →
	 *    scroll, wait for scroll to settle, then open the panel to the trigger's
	 *    configured tab (`data-active-tab-set`).
	 * 3. Has `data-overlay-reveal-id` → scroll, wait for scroll to settle, then open overlay.
	 * 4. None → scroll only.
	 * 5. Not in DOM → MutationObserver waits up to 10 s, then scrolls.
	 */
	private _deeplinkTarget(value: string, root: ShadowRoot | HTMLElement = document.body): void {
		const target = this._findByDeeplinkId(root, value)

		if (!target) {
			this._observeAndScroll(value)
			return
		}

		if (target.hasAttribute('data-expanded-content-path')) {
			this.scrollToElement(target)
			this._afterScroll(() => this._openExpandTile(target))
			return
		}

		const panelTrigger = this._findPanelTrigger(target)
		if (panelTrigger) {
			this.scrollToElement(target)
			this._afterScroll(() => this._openPanelFromTrigger(panelTrigger))
			return
		}

		const overlayId = target.getAttribute('data-overlay-reveal-id')
		if (overlayId) {
			const overlayEl = this._ensureOverlayExists(overlayId, target)
			this.scrollToElement(target)
			this._afterScroll(() => overlayEl.setAttribute('data-show', ''))
		} else {
			this.scrollToElement(target)
		}
	}

	/**
	 * Resolve the panel trigger associated with `target`: either `target` itself when it
	 * carries `data-panel-reveal-id`, or the first descendant that does (e.g. a gallery
	 * tile's `.cta-gallery-pop-detail` open button). Returns `null` if none exists.
	 */
	private _findPanelTrigger(target: HTMLElement): HTMLElement | null {
		if (target.hasAttribute('data-panel-reveal-id')) return target
		return target.querySelector<HTMLElement>('[data-panel-reveal-id]')
	}

	/**
	 * Open the panel a trigger points to by dispatching a synthetic click on the
	 * trigger itself, so the same root-scoped panel listener handles show/insert in
	 * both document and injector shadow-root contexts.
	 */
	private _openPanelFromTrigger(trigger: HTMLElement): void {
		const panelUrl = trigger.getAttribute('data-panel-reveal-id')
		if (!panelUrl) return

		// Dispatch a click so panel listeners configured on the same root (for example via
		// setupPanelShowListener(root)) handle show/insert in that DOM context.
		trigger.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }))
	}

	/**
	 * Scroll to `deeplinkId`'s trigger, wait for the page scroll to settle, open its
	 * overlay or expand-tile so the open animation is visible, then scroll inside to
	 * `scrollTargetId` once `gmx-overlay-shown` fires and the entry transition ends.
	 */
	private _deeplinkOverlayWithScroll(
		deeplinkId: string,
		scrollTargetId: string,
		root: ShadowRoot | HTMLElement = document.body,
	): void {
		const trigger = this._findByDeeplinkId(root, deeplinkId)

		if (!trigger) {
			window.console.warn(
				`GMXDeeplinkSingleton: no trigger with [data-deeplink-id="${deeplinkId}"] found on page`,
			)
			return
		}

		this.scrollToElement(trigger)

		if (trigger.hasAttribute('data-expanded-content-path')) {
			this._afterScroll(() => {
				// gmx-overlay-shown bubbles up from the inner gmx-overlay.
				trigger.addEventListener(
					'gmx-overlay-shown',
					(e: Event) => {
						const overlayEl = e.target as HTMLElement
						const containerEl =
							overlayEl.querySelector<HTMLElement>('.overlay-container') ?? overlayEl

						let done = false
						const doScroll = () => {
							if (done) return
							done = true
							clearTimeout(fallback)
							this._scrollToElementInOverlay(overlayEl, scrollTargetId)
						}

						containerEl.addEventListener('transitionend', doScroll, { once: true })
						const fallback = setTimeout(doScroll, 500)
					},
					{ once: true },
				)

				this._openExpandTile(trigger)
			})
			return
		}

		const overlayEl = this._resolveOverlayByDeeplinkId(deeplinkId, root)

		if (!overlayEl) {
			window.console.warn(
				`GMXDeeplinkSingleton: no trigger with [data-deeplink-id="${deeplinkId}"] found on page`,
			)
			return
		}

		this._afterScroll(() => {
			overlayEl.addEventListener(
				'gmx-overlay-shown',
				() => {
					const containerEl =
						overlayEl.querySelector<HTMLElement>('.overlay-container') ?? overlayEl

					let done = false
					const doScroll = () => {
						if (done) return
						done = true
						clearTimeout(fallback)
						this._scrollToElementInOverlay(overlayEl, scrollTargetId)
					}

					containerEl.addEventListener('transitionend', doScroll, { once: true })
					const fallback = setTimeout(doScroll, 500)
				},
				{ once: true },
			)

			overlayEl.setAttribute('data-show', '')
		})
	}

	/**
	 * Invoke `callback` once the page scroll settles.
	 * Uses the `scrollend` event when available, but always keeps a fixed 600 ms timeout
	 * fallback (enough for a typical smooth-scroll on most pages) in case no scroll
	 * occurs or the event does not fire.
	 */
	private _afterScroll(callback: () => void): void {
		let done = false
		let fallback: ReturnType<typeof setTimeout> | null = null

		const onScrollEnd = () => {
			finish()
		}

		const finish = () => {
			if (done) return
			done = true

			if (fallback !== null) {
				clearTimeout(fallback)
				fallback = null
			}

			if ('onscrollend' in window) {
				window.removeEventListener('scrollend', onScrollEnd)
			}

			callback()
		}

		if ('onscrollend' in window) {
			window.addEventListener('scrollend', onScrollEnd, { once: true })
		}

		fallback = setTimeout(finish, 600)
	}

	/**
	 * Call `open()` on an expand-tile, deferring until its custom element is upgraded.
	 */
	private _openExpandTile(target: HTMLElement): void {
		const tagName = target.tagName.toLowerCase()
		customElements.whenDefined(tagName).then(() => {
			;(target as HTMLElement & { open?: () => void }).open?.()
		})
	}

	/**
	 * Look up `[data-deeplink-id="deeplinkId"]`, read its `data-overlay-reveal-id`,
	 * and return (or create) the matching overlay element. Returns `null` if not found.
	 */
	private _resolveOverlayByDeeplinkId(
		deeplinkId: string,
		root: ShadowRoot | HTMLElement = document.body,
	): HTMLElement | null {
		const trigger = this._findByDeeplinkId(root, deeplinkId)
		if (!trigger) return null

		const overlayId = trigger.getAttribute('data-overlay-reveal-id')
		if (!overlayId) return null

		return this._ensureOverlayExists(overlayId, trigger)
	}

	/**
	 * Return the existing `gmx-overlay#overlayId`, or create and append one to `<body>`.
	 * Overlay type comes from `trigger[data-overlay-type]`, defaulting to `'content-fit'`.
	 */
	private _ensureOverlayExists(overlayId: string, trigger?: HTMLElement | null): HTMLElement {
		const existing = document.getElementById(overlayId)
		if (existing) return existing

		const overlayType = trigger?.getAttribute('data-overlay-type') ?? 'content-fit'

		const newOverlay = document.createElement('gmx-overlay')
		newOverlay.setAttribute('id', overlayId)
		newOverlay.setAttribute('data-overlay-type', overlayType)
		document.body.appendChild(newOverlay)

		return newOverlay
	}

	/**
	 * Scroll `overlayEl` so that `[data-deeplink-id="scrollTargetId"]` is at the top.
	 * Only called after `transitionend` / the 500 ms fallback, so rects are stable.
	 * Scrolls the overlay element directly (it is `position:fixed; overflow:auto`).
	 */
	private _scrollToElementInOverlay(overlayEl: HTMLElement, scrollTargetId: string): void {
		const target =
			this._findByDeeplinkId(overlayEl, scrollTargetId) ??
			this._findByDeeplinkId(document.body, scrollTargetId)

		if (target) {
			const overlayRect = overlayEl.getBoundingClientRect()
			const targetRect = target.getBoundingClientRect()
			const scrollTop = overlayEl.scrollTop + (targetRect.top - overlayRect.top)
			overlayEl.scrollTo({ top: scrollTop, behavior: 'smooth' })
		} else {
			window.console.warn(
				`GMXDeeplinkSingleton: scroll target '[data-deeplink-id="${scrollTargetId}"]' not found within overlay '${overlayEl.id}'`,
			)
		}
	}

	/** Return the first `[data-deeplink-id="deeplinkId"]` within `container`. */
	private _findByDeeplinkId(container: HTMLElement | ShadowRoot, deeplinkId: string): HTMLElement | null {
		return (
			Array.from(container.querySelectorAll<HTMLElement>('[data-deeplink-id]')).find(
				el => el.getAttribute('data-deeplink-id') === deeplinkId,
			) ?? null
		)
	}

	/**
	 * Smooth-scroll the page to `el`, offset by `--secondary-nav-height` so it isn't
	 * hidden behind the sticky nav. Double-rAF ensures layout is stable before reading
	 * `getBoundingClientRect()`.
	 */
	public scrollToElement(el: HTMLElement): void {
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				const navHeightStr = getComputedStyle(document.documentElement)
					.getPropertyValue('--secondary-nav-height')
					.trim()
				const navHeight = parseFloat(navHeightStr) || 0
				const top = el.getBoundingClientRect().top + window.scrollY - navHeight
				window.scrollTo({ top, behavior: 'smooth' })
			})
		})
	}

	/**
	 * Watch for `[data-deeplink-id="deeplinkId"]` via MutationObserver (up to 10 s)
	 * and scroll to it once it appears.
	 */
	private _observeAndScroll(deeplinkId: string): void {
		const observer = new MutationObserver(() => {
			const el = this._findByDeeplinkId(document.body, deeplinkId)
			if (el) {
				observer.disconnect()
				clearTimeout(timeout)
				this.scrollToElement(el)
			}
		})

		observer.observe(document.body, { childList: true, subtree: true })

		const timeout = setTimeout(() => {
			observer.disconnect()
			window.console.warn(
				`GMXDeeplinkSingleton: element '[data-deeplink-id="${deeplinkId}"]' not found within 10 s timeout`,
			)
		}, 10000)
	}
}

const deeplinkSingleton = Object.freeze(new GMXDeeplinkSingleton())
export default deeplinkSingleton
